import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Users, FileText, GraduationCap, Ticket, Sparkles, ArrowRight, BarChart3 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Provider Pro Hub — landing tab surfaces all Pro-tier tools:
 *   • CRM clients
 *   • Invoices
 *   • Courses
 *   • Coupons
 *
 * Gated by tier — if the caller is on Starter, we show upgrade CTAs instead
 * of the actual tools.
 */
export default function ProHub() {
  const [tierInfo, setTierInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState({ asProvider: [], asClient: [] });
  const [courses, setCourses] = useState([]);
  const [coupons, setCoupons] = useState([]);

  const authHeaders = () => {
    const t = localStorage.getItem("cauldron_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    (async () => {
      try {
        const t = await axios.get(`${API}/tiers/me`, { headers: authHeaders() });
        setTierInfo(t.data);
      } catch { /* not logged in */ }
    })();
  }, []);

  const isPro = tierInfo?.features?.crm_notes === true;

  useEffect(() => {
    if (!isPro) return;
    (async () => {
      try {
        const [c, i, co, cp] = await Promise.all([
          axios.get(`${API}/crm/clients`, { headers: authHeaders() }),
          axios.get(`${API}/invoices`, { headers: authHeaders() }),
          axios.get(`${API}/providers/me/courses`, { headers: authHeaders() }),
          axios.get(`${API}/coupons`, { headers: authHeaders() }),
        ]);
        setClients(c.data.clients || []);
        setInvoices(i.data || { asProvider: [], asClient: [] });
        setCourses(co.data.courses || []);
        setCoupons(cp.data.coupons || []);
      } catch (e) { /* ignore */ }
    })();
  }, [isPro]);

  if (!tierInfo) {
    return <Layout><div className="max-w-4xl mx-auto p-8 text-amber-200/60">Loading…</div></Layout>;
  }

  if (!isPro) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto p-8" data-testid="pro-hub-upsell">
          <div className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-950/40 to-neutral-900/80 p-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/40 text-amber-400 text-xs uppercase tracking-widest mb-4">
              <Sparkles className="h-3 w-3" /> Pro tier
            </div>
            <h1 className="text-3xl font-serif text-amber-100 mb-3">
              Unlock the tools that build a real business
            </h1>
            <p className="text-amber-200/70 mb-6">
              Client CRM · Auto-invoices · Courses · Coupons · Advanced analytics · Reduced 1.5% fees.
            </p>
            <div className="text-sm text-amber-200/60 mb-6">
              Current plan: <b className="text-amber-100">{tierInfo.name}</b>
            </div>
            <Link to="/pricing">
              <button className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-neutral-900 rounded-md font-medium" data-testid="pro-upgrade-cta">
                See Pro plans
              </button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="pro-hub">
        <header>
          <h1 className="text-3xl font-serif text-amber-100">Pro tools</h1>
          <p className="text-sm text-amber-200/60">
            All active — you're on the <b>{tierInfo.name}</b> plan · {tierInfo.feeBps / 100}% fee
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <ToolCard
            testid="tool-clients"
            to="/pro/clients"
            icon={<Users className="h-6 w-6" />}
            title="Clients"
            metric={`${clients.length} total`}
            hint="Notes · Tags · History"
          />
          <ToolCard
            testid="tool-invoices"
            to="/pro/invoices"
            icon={<FileText className="h-6 w-6" />}
            title="Invoices"
            metric={`${invoices.asProvider.length} issued`}
            hint="Auto-generated · PDF"
          />
          <ToolCard
            testid="tool-courses"
            to="/pro/courses"
            icon={<GraduationCap className="h-6 w-6" />}
            title="Courses"
            metric={`${courses.length} courses`}
            hint="Publish · Enroll · Progress"
          />
          <ToolCard
            testid="tool-coupons"
            to="/pro/coupons"
            icon={<Ticket className="h-6 w-6" />}
            title="Coupons"
            metric={`${coupons.length} active`}
            hint="Discount codes"
          />
          <ToolCard
            testid="tool-analytics"
            to="/pro/analytics"
            icon={<BarChart3 className="h-6 w-6" />}
            title="Analytics"
            metric="Earnings · Retention"
            hint="Cohorts + top clients"
          />
        </div>

        {/* Recent activity teaser */}
        {clients.length > 0 && (
          <Card className="p-4 bg-neutral-900/50 border-neutral-800" data-testid="recent-clients">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-amber-100 font-serif">Recent clients</h3>
              <Link to="/pro/clients" className="text-xs text-amber-400 hover:text-amber-300">See all →</Link>
            </div>
            <ul className="space-y-2">
              {clients.slice(0, 5).map(c => (
                <li key={c.clientId} className="flex items-center gap-3 py-2 border-b border-neutral-800 last:border-0" data-testid={`client-row-${c.clientId}`}>
                  <div className="w-8 h-8 rounded-full bg-amber-900/40 flex items-center justify-center text-amber-200 text-sm font-medium">
                    {(c.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-amber-100">{c.name}</div>
                    <div className="text-xs text-amber-200/50">
                      {c.bookingCount} bookings · ${c.totalSpent.toFixed(2)} lifetime
                    </div>
                  </div>
                  {c.tags?.length > 0 && (
                    <div className="flex gap-1">
                      {c.tags.slice(0, 2).map(t => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-900/30 text-amber-200">{t}</span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </Layout>
  );
}

function ToolCard({ testid, to, icon, title, metric, hint }) {
  return (
    <Link to={to} data-testid={testid}>
      <Card className="p-5 bg-neutral-900/50 border-neutral-800 hover:border-amber-500/40 transition-all group cursor-pointer h-full">
        <div className="flex items-start justify-between mb-3">
          <div className="text-amber-400">{icon}</div>
          <ArrowRight className="h-4 w-4 text-amber-200/40 group-hover:text-amber-400 transition-colors" />
        </div>
        <h3 className="text-lg font-serif text-amber-100">{title}</h3>
        <div className="text-2xl text-amber-50 font-serif mt-1">{metric}</div>
        <div className="text-xs text-amber-200/50 mt-1">{hint}</div>
      </Card>
    </Link>
  );
}
